/**
 * ДВЕ КАМЕРЫ СЦЕНЫ: ФОТОГРАФИЧЕСКАЯ И ЧЕРТЁЖНАЯ.
 *
 * До этого модуля вьюпорт рисовался ОДНОЙ `PerspectiveCamera(45, ...)`
 * (`hooks/useThreeScene.ts`), и «План», «Спереди», «Сбоку» были только
 * НАПРАВЛЕНИЯМИ взгляда этой же перспективы. На такой картинке мерить нельзя:
 * дальний край изделия короче ближнего ровно потому, что он дальше. Кухня
 * 3000 мм при камере в 4 м даёт между ближним и дальним краем разницу около
 * трети — то есть подпись «развёртка» обещала бы чертёж, которого нет.
 *
 * Поэтому слово «развёртка» было ЧЕСТНО убрано из подписей видов, а
 * `projectionWorkPlane.test.ts` держал запрет на чертёжные слова — до тех пор,
 * пока камеры нет. Здесь эта камера появляется, и запрет снимается вместе с
 * причиной, по которой он стоял.
 *
 * ПРАВИЛО РАЗДЕЛА. Плоская проекция (`ProjectionWorkPlane.flat`) — это рабочая
 * ПОВЕРХНОСТЬ: две живые оси, третья заморожена глубиной кадра. По такой
 * поверхности человек меряет, поэтому она обязана быть ортографической.
 * Объёмные виды (перспектива, изометрия) — это ВЗГЛЯД, а не чертёж: они
 * остаются перспективными, и мерить по ним по-прежнему нельзя.
 *
 * ОДИН ВЛАДЕЛЕЦ ПЕРЕКЛЮЧЕНИЯ. Камера — общий ресурс сцены: её читают гизмо
 * перемещения, линейка, цепочки размеров, HUD, выделение и трассировщик
 * снимков. Второй таблицы «когда какая камера» быть не должно: разойдясь,
 * «чем рисуем» и «чем меряем» стали бы спорить, и человек получил бы число,
 * снятое не с той картинки, которую видит.
 *
 * ЗУМ В ОРТОГРАФИИ СЧИТАЕТСЯ ИНАЧЕ. У перспективы кадр решает РАССТОЯНИЕ, у
 * ортографии — РАЗМЕР ФРУСТУМА. Поэтому `fitCameraToBox` (перспективная
 * подгонка по восьми углам) здесь не переиспользуется, а повторяется в своей
 * ортографической форме: тот же отбор по восьми углам, но результат — половина
 * высоты кадра в миллиметрах, а не дистанция. Дистанция в ортографии влияет
 * ТОЛЬКО на отсечение near/far, и её нельзя брать «на глаз»: слишком близкая
 * камера срежет половину изделия, не изменив масштаб.
 */

import * as THREE from 'three';
import { FULL_FRAME_BAND, type VisibleBand } from './viewportInsets';

export type SceneCamera = THREE.PerspectiveCamera | THREE.OrthographicCamera;

/** Проекции вьюера — те же ключи, что у `Projection` в сторе и у рабочей плоскости. */
export type CameraProjection = 'perspective' | 'iso' | 'top' | 'front' | 'side';

/**
 * Плоские проекции — и ровно они рисуются ортографической камерой.
 *
 * Список НЕ выводится из `projectionWorkPlane` импортом: тот слой намеренно
 * оставлен без зависимостей и без THREE. Согласие двух таблиц держит пин
 * `sceneCameras.test.ts` — он читает обе и краснеет на расхождении.
 */
export const ORTHOGRAPHIC_PROJECTIONS: readonly CameraProjection[] = ['top', 'front', 'side'];

export function isOrthographicProjection(projection: CameraProjection): boolean {
  return ORTHOGRAPHIC_PROJECTIONS.includes(projection);
}

/**
 * Базис кадра по направлению взгляда — тот же, что у перспективной подгонки
 * (`geometry-helpers.fitCameraToBox`). Повторён здесь, а не импортирован,
 * потому что там он живёт внутри функции; расхождение ловит пин на одинаковых
 * числах для одной и той же коробки.
 */
export function cameraFrameBasis(direction: THREE.Vector3): {
  forward: THREE.Vector3;
  right: THREE.Vector3;
  up: THREE.Vector3;
} {
  const forward = direction.clone().normalize().negate();
  const worldUp = Math.abs(forward.y) > 0.999 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
  const right = new THREE.Vector3().crossVectors(forward, worldUp).normalize();
  const up = new THREE.Vector3().crossVectors(right, forward).normalize();
  return { forward, right, up };
}

export type OrthographicFrame = Readonly<{
  /** Половина высоты кадра в МИЛЛИМЕТРАХ проекта — это и есть масштаб чертежа. */
  halfHeightMm: number;
  /** Дистанция до центра: в ортографии она не масштабирует, а только держит near/far. */
  distanceMm: number;
  /** Половина глубины коробки вдоль взгляда — чтобы near/far накрыли её целиком. */
  halfDepthMm: number;
  /**
   * На сколько миллиметров цель камеры уведена ВНИЗ и ВЛЕВО от центра коробки,
   * чтобы коробка встала в середину ВИДИМОЙ полосы, а не в середину кадра.
   *
   * Ноль на открытом холсте. Отдельные поля, а не «уже применено внутрь»,
   * потому что смещение цели — часть замера: по нему видно, НАСКОЛЬКО кадр
   * смещён, и его можно проверить числом, не разбирая позу камеры.
   */
  targetOffsetUpMm: number;
  targetOffsetRightMm: number;
}>;

/**
 * ОТКАЗ ВМЕСТО ВЫДУМАННОГО КАДРА.
 *
 * Пустая, нечисловая или вырожденная коробка не подменяется «кадром по
 * умолчанию»: подставленный масштаб означал бы, что человек меряет по числу,
 * которого никто не объявлял.
 */
export const ORTHOGRAPHIC_FRAME_REFUSAL = 'SCENE_CAMERA_ORTHOGRAPHIC_BOX_UNMEASURABLE';

/**
 * Кадр ортографии по восьми углам коробки: сколько миллиметров обязано
 * поместиться по высоте и по ширине экрана.
 *
 * @param aspect ширина/высота холста; неположительное значение — отказ, а не 1:
 *               кадр по выдуманному соотношению даёт неверный масштаб молча.
 * @param band   видимая полоса кадра — часть холста, не закрытая хромом
 *               оболочки. По умолчанию весь кадр: тогда считается ровно то же,
 *               что считалось до появления полосы.
 */
export function orthographicFrame(
  box: THREE.Box3,
  direction: THREE.Vector3,
  aspect: number,
  padding = 1,
  band: VisibleBand = FULL_FRAME_BAND,
): OrthographicFrame {
  if (!Number.isFinite(aspect) || aspect <= 0) throw new Error(`${ORTHOGRAPHIC_FRAME_REFUSAL}:aspect`);
  if (!Number.isFinite(padding) || padding <= 0) throw new Error(`${ORTHOGRAPHIC_FRAME_REFUSAL}:padding`);
  const corners = [box.min.x, box.max.x, box.min.y, box.max.y, box.min.z, box.max.z];
  if (!corners.every(Number.isFinite)) throw new Error(`${ORTHOGRAPHIC_FRAME_REFUSAL}:box`);
  if (box.isEmpty()) throw new Error(`${ORTHOGRAPHIC_FRAME_REFUSAL}:empty`);

  const center = box.getCenter(new THREE.Vector3());
  const { forward, right, up } = cameraFrameBasis(direction);

  let halfUp = 0;
  let halfRight = 0;
  let halfDepth = 0;
  for (let corner = 0; corner < 8; corner += 1) {
    const point = new THREE.Vector3(
      corner & 1 ? box.max.x : box.min.x,
      corner & 2 ? box.max.y : box.min.y,
      corner & 4 ? box.max.z : box.min.z,
    ).sub(center);
    halfUp = Math.max(halfUp, Math.abs(point.dot(up)));
    halfRight = Math.max(halfRight, Math.abs(point.dot(right)));
    halfDepth = Math.max(halfDepth, Math.abs(point.dot(forward)));
  }

  // Кадр обязан накрыть коробку и по высоте, и по ширине: ширина переводится в
  // высоту делением на соотношение сторон холста.
  //
  // Полоса входит сюда делением, а не вычитанием: `halfY = 0.62` означает, что
  // видно 62 % высоты кадра, поэтому кадр обязан стать во столько же раз выше,
  // чтобы коробка уложилась в видимую часть. В ортографии всё проще, чем в
  // перспективе: расстояние на масштаб не влияет, и отклонение угла от центра
  // полосы не зависит от глубины — уравнение решается без неизвестной.
  const halfY = band.halfY > 0 ? band.halfY : 1;
  const halfX = band.halfX > 0 ? band.halfX : 1;
  const halfHeightMm = Math.max(1, Math.max(halfUp / halfY, halfRight / (halfX * aspect)) * padding);
  // Дистанция не масштабирует — она отодвигает камеру так, чтобы вся глубина
  // осталась между near и far с запасом на орбиту.
  const distanceMm = Math.max(1, (halfDepth + halfHeightMm) * 2);
  // Центр полосы выражен в долях полукадра, поэтому в миллиметры он переводится
  // умножением на полувысоту (и на полуширину = полувысота × aspect).
  return Object.freeze({
    halfHeightMm,
    distanceMm,
    halfDepthMm: halfDepth,
    targetOffsetUpMm: band.centerY * halfHeightMm,
    targetOffsetRightMm: band.centerX * halfHeightMm * aspect,
  });
}

/**
 * Перенести половину высоты кадра в границы камеры. Вызывается и при подгонке,
 * и на КАЖДОМ изменении размера холста: у ортографии соотношение сторон живёт
 * в left/right, а не в отдельном поле `aspect`, поэтому забытый ресайз
 * растягивает чертёж — и замер по нему становится неверным.
 */
export function syncOrthographicFrustum(
  camera: THREE.OrthographicCamera,
  halfHeightMm: number,
  aspect: number,
): void {
  if (!Number.isFinite(halfHeightMm) || halfHeightMm <= 0) throw new Error(`${ORTHOGRAPHIC_FRAME_REFUSAL}:halfHeight`);
  if (!Number.isFinite(aspect) || aspect <= 0) throw new Error(`${ORTHOGRAPHIC_FRAME_REFUSAL}:aspect`);
  camera.top = halfHeightMm;
  camera.bottom = -halfHeightMm;
  camera.right = halfHeightMm * aspect;
  camera.left = -halfHeightMm * aspect;
  camera.updateProjectionMatrix();
}

/**
 * Подогнать ортографическую камеру под коробку в заданном направлении.
 * Возвращает применённый кадр — вызывающий обязан запомнить `halfHeightMm`,
 * чтобы ресайз холста пересчитал left/right от ТОЙ ЖЕ высоты.
 */
export function fitOrthographicCameraToBox(
  camera: THREE.OrthographicCamera,
  controls: { target: THREE.Vector3; update(): void },
  box: THREE.Box3,
  aspect: number,
  padding = 1.12,
  direction?: THREE.Vector3,
  band: VisibleBand = FULL_FRAME_BAND,
): OrthographicFrame {
  const dir = direction?.clone().normalize() ?? new THREE.Vector3(1, 0.75, 1).normalize();
  const frame = orthographicFrame(box, dir, aspect, padding, band);
  const { right, up } = cameraFrameBasis(dir);
  // Цель уводится от центра коробки на ту же величину, на какую видимая полоса
  // смещена от центра кадра, — иначе чертёж встал бы в середину холста и
  // наполовину уехал под ленту этапов, как это и было.
  const center = box.getCenter(new THREE.Vector3())
    .addScaledVector(up, -frame.targetOffsetUpMm)
    .addScaledVector(right, -frame.targetOffsetRightMm);

  camera.position.copy(center.clone().add(dir.multiplyScalar(frame.distanceMm)));
  // Зум — это ручка ЧЕЛОВЕКА (колесо мыши в OrbitControls), а не способ подгонки.
  // Подгонка выражается размером фрустума; оставленный зум прошлого вида увёл бы
  // масштаб нового кадра в неизвестное число.
  camera.zoom = 1;
  // near может быть отрицательным у ортографии, но отрицательный near пускает
  // в кадр геометрию ЗА камерой — в плане это соседняя комната поверх изделия.
  camera.near = 1;
  camera.far = frame.distanceMm + frame.halfDepthMm + frame.halfHeightMm * 4;
  syncOrthographicFrustum(camera, frame.halfHeightMm, aspect);
  camera.lookAt(center);
  camera.updateMatrixWorld(true);
  controls.target.copy(center);
  controls.update();
  return frame;
}

/**
 * Цена пикселя в миллиметрах на ЭТОЙ камере — ровно та величина, которой
 * линейка задаёт радиус притяжения, а размерные цепочки — толщину выносок.
 *
 * Смысл разделения виден именно здесь: у ортографии число ОДНО на весь кадр
 * (высота кадра делить на высоту холста), у перспективы оно зависит от
 * глубины точки — и потому «тот же отрезок» у дальнего края даёт меньше
 * пикселей, чем у ближнего. Это и есть причина, по которой по перспективе
 * нельзя мерить.
 *
 * Неизвестная величина — null, а не число: выдуманный масштаб хуже
 * отсутствующего.
 */
export function orthographicMmPerPixel(
  camera: THREE.OrthographicCamera,
  canvasHeightPx: number,
): number | null {
  if (!Number.isFinite(canvasHeightPx) || canvasHeightPx <= 0) return null;
  const zoom = Number.isFinite(camera.zoom) && camera.zoom > 0 ? camera.zoom : null;
  if (zoom === null) return null;
  const heightMm = (camera.top - camera.bottom) / zoom;
  if (!Number.isFinite(heightMm) || heightMm <= 0) return null;
  return heightMm / canvasHeightPx;
}

/**
 * Перенести позу с одной камеры на другую при переключении проекции.
 *
 * Переключение вида НЕ обязано терять, куда человек смотрел: после «План» →
 * «Перспектива» камера обязана стоять там же, а не прыгать в начало координат.
 * Поза — это положение, ориентация и «верх»; масштаб (fov против фрустума) у
 * камер разный по природе и переносу не подлежит — его задаёт подгонка.
 */
/**
 * ЭКРАННЫЙ ПРЯМОУГОЛЬНИК КОРОБКИ ПОСЛЕ ПРИМЕНЁННОГО КАДРА.
 *
 * Это ЗАМЕР, а не предсказание: считается по той самой матрице, которой сцена
 * сейчас рисуется. Второй способ посчитать то же самое («мы же знаем формулу
 * кадра») разошёлся бы с камерой на первой же правке подгонки, и расхождение
 * было бы молчаливым — картинка правдоподобна, число неверно.
 *
 * Отказ (null) честнее числа там, где проекция не определена: у перспективы
 * угол ЗА камерой даёт зеркальные координаты, и прямоугольник по ним описывал
 * бы не модель, а её отражение.
 */
export function projectedBoxRect(
  box: THREE.Box3,
  camera: SceneCamera,
  canvasRect: { left: number; top: number; width: number; height: number },
): { left: number; top: number; right: number; bottom: number; width: number; height: number } | null {
  if (!(canvasRect.width > 0) || !(canvasRect.height > 0)) return null;
  if (box.isEmpty()) return null;
  camera.updateMatrixWorld(true);
  const viewProjection = new THREE.Matrix4()
    .multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (let corner = 0; corner < 8; corner += 1) {
    const point = new THREE.Vector4(
      corner & 1 ? box.max.x : box.min.x,
      corner & 2 ? box.max.y : box.min.y,
      corner & 4 ? box.max.z : box.min.z,
      1,
    ).applyMatrix4(viewProjection);
    if (!(point.w > 1e-6)) return null;
    const ndcX = point.x / point.w;
    const ndcY = point.y / point.w;
    const px = canvasRect.left + (ndcX * 0.5 + 0.5) * canvasRect.width;
    const py = canvasRect.top + (0.5 - ndcY * 0.5) * canvasRect.height;
    minX = Math.min(minX, px); maxX = Math.max(maxX, px);
    minY = Math.min(minY, py); maxY = Math.max(maxY, py);
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;
  return { left: minX, top: minY, right: maxX, bottom: maxY, width: maxX - minX, height: maxY - minY };
}

export function copyCameraPose(from: SceneCamera, to: SceneCamera): void {
  to.position.copy(from.position);
  to.quaternion.copy(from.quaternion);
  to.up.copy(from.up);
  to.updateMatrixWorld(true);
}
